
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const APP_STORE_URL = 'https://apps.apple.com/us/app/starlightsaga/id6757761088';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mysagallc.thisismysaga';

const DownloadButtons = ({ className = '' }) => (
  <div className={`download-buttons ${className}`}>
    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge store-badge-apple" aria-label="Download on the App Store">
      <img src={`${process.env.PUBLIC_URL}/app-store-badge.svg`} alt="Download on the App Store" />
    </a>
    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge store-badge-google" aria-label="Get it on Google Play">
      <img src={`${process.env.PUBLIC_URL}/google-play-badge.png`} alt="Get it on Google Play" />
    </a>
  </div>
);

const TRUST_ITEMS = [
  {
    label: 'Private by default',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </svg>
    ),
  },
  {
    label: 'End-to-end encrypted',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    label: 'No ads',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M5.6 5.6l12.8 12.8" />
      </svg>
    ),
  },
  {
    label: '50% to charity',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />
      </svg>
    ),
  },
];

const TrustStrip = () => (
  <section className="trust-strip">
    <ul className="trust-list">
      {TRUST_ITEMS.map(({ label, icon }) => (
        <li key={label} className="trust-item">
          <span className="trust-icon">{icon}</span>
          <span className="trust-label">{label}</span>
        </li>
      ))}
    </ul>
  </section>
);

const StarRating = ({ caption }) => (
  <div className="star-rating" role="img" aria-label={`5 out of 5 stars. ${caption}`}>
    <div className="stars" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} className="star" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.5l2.92 5.92 6.53.95-4.72 4.6 1.11 6.5L12 17.4 6.16 20.47l1.11-6.5L2.55 9.37l6.53-.95L12 2.5z" />
        </svg>
      ))}
    </div>
    <span className="star-caption">"{caption}"</span>
  </div>
);

const FEATURES = [
  {
    caption: 'Inspiration to guide your day.',
    image: 'mockup-daily-quote.png',
  },
  {
    caption: 'See the patterns shaping your life.',
    image: 'mockup-dashboard.png',
  },
  {
    caption: 'Gentle prompts to clarify your life purpose.',
    image: 'mockup-prompts.png',
  },
  {
    caption: 'End-to-end encryption. Your data stays yours.',
    image: 'mockup-encryption.png',
  },
];

const FAQS = [
  {
    question: 'Why only $1/month?',
    answer: "Journaling shouldn't be a luxury. $1/month keeps StarlightSaga sustainable while staying accessible to anyone who wants to start reflecting on their life — and 50% (not a typo) goes to charity.",
  },
  {
    question: 'Who is this for?',
    answer: "StarlightSaga is for anyone who wants to slow down, make sense of their day, and live more intentionally. The prompts are therapist-designed, so it works whether you're new to journaling or have been keeping a notebook for years.",
  },
  {
    question: 'Can I export my journal?',
    answer: 'Yes. You own your entries and can export them at any time. We also let you delete your data permanently whenever you want — no hoops, no email back-and-forth.',
  },
  {
    question: 'Do you sell my data?',
    answer: 'Never. We make money from your $1/month subscription, not from your data. You can also turn on end-to-end encryption so that even we can\'t read your entries.',
  },
  {
    question: 'Is it private?',
    answer: 'Yes, no one else has access to your data. You can choose to encrypt your data from end-to-end, making it safe from hackers, and you can export and delete it at any time.',
  },
  {
    question: 'Which charity do you donate to?',
    answer: "As part of our effort to support our local & broader community as we grow, we donate 50% of proceeds to Put People First! PA, an organization that helps people who can't afford healthcare.",
  },
];

const Home = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleFaq = (i) => setActiveFaq(activeFaq === i ? null : i);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const response = await fetch('https://formspree.io/f/xqepljzq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          _replyto: formData.email,
          _subject: `Contact form message from ${formData.name}`,
        }),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <>
      <section id="home" className="hero">
        <div className="hero-overlay"></div>
        <header className="header">
          <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="StarlightSaga logo" className="hero-logo-icon" />
          <h2 className="logo">StarlightSaga</h2>
          <h3 className="header-text">A journal that connects the dots. Turn reflection into insight.</h3>
          <StarRating caption="Great for mental health" />
          <DownloadButtons />
          <TrustStrip />
          <button className="continue-button" onClick={goToFeatures}>More Info</button>
        </header>
      </section>

      <section id="features" className="features-section">
        <div className="photos-container reveal reveal-stagger">
          {FEATURES.map(({ caption, image }) => (
            <div key={image} className="photo-and-caption">
              <img src={`${process.env.PUBLIC_URL}/${image}`} alt={caption} className="img" />
              <h2>{caption}</h2>
            </div>
          ))}
        </div>
      </section>

      <section id="about" className="about-section">
        <div className="bio-section reveal">
          <div className="bio-text">
            <h2>About Me</h2>
            <p>I'm Alice Onuffer, the founder of StarlightSaga. I live in Pittsburgh, PA, with my husband.</p>
            <p>I'm a software engineer and former therapist.</p>
            <p>I built StarlightSaga because I wanted a way to preserve the moments, emotions, and realizations that shape a life — before they disappear from mind.</p>
          </div>
          <img src={`${process.env.PUBLIC_URL}/me.jpg`} alt="Alice Onuffer" className="bio-image" />
        </div>

        <div className="faq-section">
          <h2 className="reveal">Frequently Asked Questions</h2>
          <div className="faq-list reveal reveal-stagger">
            {FAQS.map((faq, index) => (
              <div key={index} className="faq-item">
                <div className="faq-question" onClick={() => toggleFaq(index)}>
                  <h3>{faq.question}</h3>
                  <span className="faq-icon">{activeFaq === index ? '−' : '+'}</span>
                </div>
                <div className={`faq-answer ${activeFaq === index ? 'open' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="contact-section">
        <div className="contact-content reveal">
          <h2>Contact Us</h2>
          <p className="contact-description">
            Have questions, feedback, or just want to say hello? We'd love to hear from you.
          </p>

          {status === 'success' ? (
            <div className="contact-success">
              <h3>Thank you!</h3>
              <p>Your message has been sent. We'll get back to you soon.</p>
              <button onClick={() => setStatus('idle')}>Send another message</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="Your name" />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="5" placeholder="How can we help you?" />
              </div>
              {status === 'error' && (
                <p className="contact-error">
                  Something went wrong. Please try again or email us directly at{' '}
                  <a href="mailto:support@starlightsaga.com">support@starlightsaga.com</a>
                </p>
              )}
              <button type="submit" className="contact-submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}

          <p className="contact-alternate">
            Or email us directly at <a href="mailto:support@starlightsaga.com">support@starlightsaga.com</a>
          </p>
        </div>
      </section>

      <section className="final-cta">
        <video autoPlay muted loop playsInline className="background-video">
          <source src={`${process.env.PUBLIC_URL}/background.mp4`} type="video/mp4" />
        </video>
        <div className="final-cta-overlay"></div>
        <div className="final-cta-content reveal">
          <h2>Ready to Live Your Saga?</h2>
          <p>Join us. $1/month. 50% goes to charity.</p>
          <DownloadButtons className="final-cta-buttons" />
        </div>
      </section>

      <footer className="site-footer">
        <Link to="/privacy">Privacy Policy</Link>
        <span className="footer-separator">|</span>
        <Link to="/terms">Terms of Use</Link>
        <span className="footer-separator">|</span>
        <Link to="/cookies">Cookie Policy</Link>
        <span className="footer-separator">|</span>
        <button
          type="button"
          className="footer-link-button"
          // Termly can't see links React adds after it loads, so open its modal directly.
          onClick={() => window.displayPreferenceModal?.()}
        >
          Cookie Preferences
        </button>
      </footer>
    </>
  );
};

export default Home;
